import Head from "next/head";
import Link from "next/link";

import { routes } from "src/shared/routes";
import { constants } from "src/shared/constants";
import { useRouterExt } from "src/components/reusable/hooks/useRouterExt";
import { useTeamConfig } from "src/components/reusable/components/TeamConfigProvider";

import type { TTeamConfig } from "src/shared/types/teamConfig.types";

const StripersLink = () => (
  <Link target="_blank" href="https://am.ticketmaster.com/gwinnettstripers/">
    Account Manager
  </Link>
);

export const FAQS: TTeamConfig["LANGUAGES"]["FAQS"] = [
  {
    question:
      "How do I claim my tickets? When does the check-in window open before each game?",
    answer:
      "You will receive a text when the window to check-in is open. The check-in window starts at 9 am the day before the first game of a homestand. You will have an opportunity to check-in for all the games in the series.",
  },
  {
    question:
      "Do I get a ticket to each game on the schedule? Are there penalties if I do not check-in?",
    answer:
      "Tickets are based on availability for each game on the schedule. There are no penalties for not attending a game, however you do need to confirm your attendance in order to receive a ticket.",
  },
  {
    question: "Where are my tickets?",
    answer:
      "Your tickets will be sent over 3 hours prior to game start and you can find the tickets within your Stripers Team Account.",
  },
  {
    question: "How are tickets sent to me?",
    answer: (
      <>
        3 hours prior to game start you will receive a text confirming that
        tickets have been delivered to your Stripers <StripersLink />.
      </>
    ),
  },
  {
    question: "Where will my seats be located?",
    answer:
      "Seat Locations are General Admission Lawn or Baseline Box. Opportunities to upgrade may be available, based upon availability.",
  },
  {
    question: "Can these tickets be transferred to a friend?",
    answer:
      "Stripers Summer Pass tickets are not eligible for resale. Abusing these guidelines can result in your Stripers Summer Pass being revoked.",
  },
  {
    question: "Can my seats be changed?",
    answer:
      "Once tickets have been sent to your account, they cannot be changed. Please contact tickets@gostripers.com for any assistance you may require.",
  },
  {
    question: "Can I receive a refund if I cannot attend a game?",
    answer: "We do not issue any refunds for games you are not able to attend.",
  },
  {
    question: "How can I get a refund and cancel my Stripers Summer Pass?",
    answer:
      "Please contact tickets@gostripers.com for any requests for refunds or cancellations.",
  },
];

export const TERMS_AND_CONDITION = () => (
  <>
    By purchasing you agree to receive SMS alerts regarding ticket delivery and
    agree to the{" "}
    <Link
      target={"_blank"}
      href={
        "https://cdn.prod.website-files.com/66b3d5184e88b53f24e71360/6836259e2aac5657874b9955_Attend%20Terms%20of%20Use%20052425.pdf"
      }
    >
      Terms & Conditions
    </Link>{" "}
    and{" "}
    <Link
      target={"_blank"}
      href={
        "https://cdn.prod.website-files.com/66b3d5184e88b53f24e71360/683625a812daf6ceadcbe532_Attend%20Privacy%20Policy%20052725.pdf"
      }
    >
      Privacy Policy
    </Link>
    .
  </>
);

export const PASS_PURCHASE_SUCCESS_MESSAGE: TTeamConfig["LANGUAGES"]["PASS_PURCHASE_SUCCESS_MESSAGE"] =
  {
    TITLE: () => (
      <>
        You&apos;e all set to experience the action! Don&apos;t forget to
        confirm attendance here on game day!
      </>
    ),
    DESCRIPTION: () => {
      const router = useRouterExt();
      return (
        <>
          Friends don&apos;t leave friends behind.{" "}
          <b>
            <u>
              <span
                onClick={() => router.pushSchema(routes.my_group)}
                style={{ cursor: "pointer" }}
              >
                Invite your friends
              </span>
            </u>
          </b>{" "}
          and enjoy games together.
        </>
      );
    },
  };

export const AnalyticsHeader = () => {
  const { TEAM_SCHEMA } = useTeamConfig();

  return (
    <Head>
      <script
        nonce={process.env.NEXT_PUBLIC_CSP_NONCE}
        defer
        id={`${TEAM_SCHEMA}-pixel-script`}
        dangerouslySetInnerHTML={{
          __html: `
                !function(f,b,e,v,n,t,s)
                {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
                n.callMethod.apply(n,arguments):n.queue.push(arguments)};
                if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
                n.queue=[];t=b.createElement(e);t.async=!0;
                t.src=v;s=b.getElementsByTagName(e)[0];
                s.parentNode.insertBefore(t,s)}(window, document,'script',
                'https://connect.facebook.net/en_US/fbevents.js');
                fbq('init', '2798649903678542');
                fbq('track', 'PageView');
                window.addEventListener("${constants.customEvents.purchaseSuccess}", async function(e) {
                  try {
                    const { cartRes } = e.detail;
  
                    fbq('track', 'Purchase', {
                      data: [
                        {
                          event_name: "Purchase",
                          event_time: Date.now(),
                          action_source: "website",
                          user_data: {
                            em: ["7b17fb0bd173f625b58636fb796407c22b3d16fc78302d79f0fd30c2fc2fc068",],
                            ph: [null],
                          },
                          attribution_data: {
                            attribution_share: "0.3",
                          },
                          custom_data: {
                            currency: "USD",
                            value: String(cartRes.totalAmount),
                          },
                          original_event_data: {
                            event_name: "Purchase",
                            event_time: Date.now(),
                          },
                        },
                      ],
                    });
                  } catch(error){
                    console.log('meta pixel purchase event error:', error); 
                  }
                });
              `,
        }}
      />
      <noscript>
        <img
          height="1"
          width="1"
          style={{ display: "none" }}
          src="https://www.facebook.com/tr?id=1800332046857750&ev=PageView&noscript=1"
        />
      </noscript>
    </Head>
  );
};
